import React, { Component } from "react";
import PropTypes from "prop-types";

import * as presetsRepo from "../../../shared/repos/graphql/presets";
import { defaultCurrency } from "../../../shared/constants/currency";
import amplitudeAnalytics from "../../../shared/utilities/amplitude";
import brStyles from "./PresetBoxesBrandRefresh.module.css";

class PresetBoxes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      presets: [],
      selected: null
    };

    this.presetBoxRef = React.createRef(); // create self reference
  }

  async componentDidMount() {
    const { productId, storeId, includePersonalizedPreset, isBrandRefresh } = this.props;
    const presets = await presetsRepo.getPresets(+productId,+storeId,includePersonalizedPreset);
    const presetList = presets?.data?.presets || [];
    this.setState({
      presets: presetList,
    });
    const el = this.presetBoxRef.current;
    if (el) {
      if (this.shouldEnableHorizontalScroll(presetList.length, isBrandRefresh)) {
        el.addEventListener("wheel", this.onScroll, { passive: false });
      }

      el.addEventListener("touchmove", (e) => {
        e.stopPropagation();
      }, { passive: true });
    }

    this.defaultSelected();
  }

  componentWillUnmount() {
    const el = this.presetBoxRef.current;
    if (el) {
      el.removeEventListener("wheel", this.onScroll);
    }
  }

  onScroll = e => {
    const el = this.presetBoxRef.current;
    if (!el || e.deltaY === 0) return;

    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    // deltaY > 0 means "scroll content down" = carousel scrolls content right = carousel scrollLeft increases
    const scrollingRight = e.deltaY > 0;
    const scrollingLeft = e.deltaY < 0;

    // If the carousel cannot scroll further in the requested direction, let the
    // browser handle the event so the page can scroll instead of being eaten.
    if ((scrollingRight && atEnd) || (scrollingLeft && atStart)) return;

    e.preventDefault();
    el.scrollTo({
      left: el.scrollLeft + e.deltaY,
      behavior: "smooth"
    });
  };

  shouldEnableHorizontalScroll = (presetCount, isBrandRefresh) => {
    if (!isBrandRefresh) return true;
    if (typeof window === "undefined" || !window.matchMedia) return true;
    const isMobile = window.matchMedia("(max-width: 991px)").matches;
    return isMobile ? presetCount > 2 : presetCount > 3;
  };

  selectPreset = (preset, trackFilterEvent = false) => {
    const { onSelect, pdpProductId, pdpProductTitle } = this.props;
    if (onSelect) {
      onSelect(preset);
    }

    this.setState({
      selected: preset.id,
    });

    if (
      trackFilterEvent
      && preset?.title
      && pdpProductId != null
      && String(pdpProductId).trim() !== ""
    ) {
      amplitudeAnalytics.trackFilterPdp({
        productName: pdpProductTitle,
        productId: pdpProductId,
        filterName: preset.title,
      });
    }
  };

  onClear = (e) => {
    e.preventDefault()
    const { onClear } = this.props;
    onClear();
    this.setState({
      selected: null
    });
  };

  defaultSelected = () => {
    const { selectedPreset } = this.props;
    const { presets } = this.state;
    if (selectedPreset) {
      const preset = presets.find(p => p.slug === selectedPreset);
      if (preset) {
        this.selectPreset(preset, false);
      }
    }
  }

  render() {
    const { presets, selected } = this.state;
    const { currency, isBrandRefresh } = this.props;

    if (presets.length <= 0) {
      return null;
    }

    const headingClass = isBrandRefresh
      ? brStyles.heading
      : "text-dark dark:text-white font-montserratMedium font-bold text-lg leading-[22px] tracking-[-0.1px]";
    const clearClass = isBrandRefresh
      ? brStyles.clear
      : "text-[#983992] font-montserratBold text-lg leading-[22px] tracking-[-0.1px] cursor-pointer";

    return (
      <div data-part={isBrandRefresh ? "presets" : undefined}>
        <div
          className={
            isBrandRefresh
              ? brStyles.headerRow
              : "flex justify-between items-center mt-2 gap-3"
          }
        >
          <h2 className={headingClass}>
            Start with one of our most popular assortments
          </h2>
          <div
            role="button"
            tabIndex={0}
            onClick={this.onClear}
            onKeyPress={this.onClear}
            className={clearClass}
          >
            {isBrandRefresh ? "Clear" : "clear selections"}
          </div>
        </div>
        <section
          ref={this.presetBoxRef}
          style={
            isBrandRefresh
              ? {
                  WebkitOverflowScrolling: "touch",
                  touchAction: "manipulation"
                }
              : {
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                  touchAction: "manipulation",
                  display: "flex",
                  overflowX: "auto",
                  overflowY: "hidden",
                  position: "relative",
                  scrollSnapType: "x mandatory"
                }
          }
          className={
            isBrandRefresh
              ? brStyles.carousel
              : "no-scrollbar flex flex-nowrap gap-2 overflow-x-scroll max-w-max snap-x mt-2"
          }
          data-scroll-desktop={
            isBrandRefresh && presets.length > 3 ? "true" : "false"
          }
          data-scroll-mobile={
            isBrandRefresh && presets.length > 2 ? "true" : "false"
          }
          onTouchStart={(e) => e.stopPropagation()}
        >
          {presets.map(preset => {

            let upcharge = 0;
            preset.optionGroups.forEach(group => {
              group.options.forEach(option => {
                upcharge += option.price * option.quantity;
              });
            });

            const active = selected === preset.id;
            let presetSelectedAttr;
            if (isBrandRefresh) {
              presetSelectedAttr = active ? "true" : "false";
            }
            const cardClass = isBrandRefresh
              ? `${brStyles.card} ${active ? brStyles.cardSelected : ""}`
              : `flex-none w-[171px] h-[149px] snap-always snap-center
                 text-dark dark:text-white
                hover:bg-light
                dark:hover:bg-dark
                cursor-pointer border-[1px] ${
                  active ? "bg-light dark:bg-dark" : ""
                } rounded-xl px-1 py-2 border-[#E6DFE8]`;
            const titleClass = isBrandRefresh
              ? brStyles.title
              : "text-dark dark:text-white leading-5 font-bold font-montserratMedium text-center";
            const actionClass = isBrandRefresh
              ? brStyles.action
              : "text-border dark:text-[#FF70A8] text-sm leading-4 font-montserratMedium cursor-pointer font-bold";
            const upchargeClass = isBrandRefresh
              ? brStyles.upcharge
              : "text-dark dark:text-white text-sm leading-4 font-montserratMedium cursor-pointer font-bold";

            return (
              <div
                className={cardClass}
                key={preset.id}
                data-part={isBrandRefresh ? "preset-card" : undefined}
                data-selected={presetSelectedAttr}
              >
                <div
                  className="flex flex-col gap-1 items-center justify-center"
                  onClick={() => this.selectPreset(preset, true)}
                  onKeyPress={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      this.selectPreset(preset, true);
                    }
                  }}
                  role="button"
                  tabIndex={preset.id}
                >
                  <div className={titleClass}>
                    {preset.title}
                  </div>
                  <div className={isBrandRefresh ? brStyles.photo : "h-[67px]"}>
                    {preset.image && (
                      <img
                        src={preset.image}
                        alt={preset.title}
                        className={
                          isBrandRefresh
                            ? brStyles.photoImg
                            : "h-[67px] object-contain"
                        }
                      />
                    )}
                  </div>
                  <p className={actionClass}>
                    {active ? "Selected" : "Select"}
                  </p>
                  {upcharge > 0 && (
                    <p className={upchargeClass}>
                      {`(+${currency?.symbol}${upcharge.toFixed(2)})`}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      </div>
    );
  }
}

PresetBoxes.propTypes = {
  productId: PropTypes.number.isRequired,
  storeId: PropTypes.number.isRequired,
  includePersonalizedPreset: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  currency: PropTypes.objectOf(PropTypes.string),
  selectedPreset: PropTypes.string,
  pdpProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pdpProductTitle: PropTypes.string,
  isBrandRefresh: PropTypes.bool,
};

PresetBoxes.defaultProps = {
  currency: defaultCurrency,
  selectedPreset: null,
  pdpProductId: null,
  pdpProductTitle: null,
  isBrandRefresh: false,
};

export default PresetBoxes;
